import { Formik, Form as FormikForm, FormikHelpers, FormikProps } from "formik";
import React, { useRef } from "react";
import { Button, Col, Spinner, Row } from "react-bootstrap";
import { PlusLg } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import {
  DomainStatusByApiQuery,
  useSetDomainUrlMutation,
} from "../../../../graphql/projects.hooks";
import AddDomainLogo from "../img/addDomain.webp";
import DomainConnectView from "../domainConnectView";
import {
  validationDomain,
  validationSubdomain,
} from "../../../../validations/newProject";
import DomainStatus from "../domainStatus";
import VerifyingInfoView from "../verifyingInfoView";
import useGetUrlLocale from "../../../../hooks/useGetUrlLocale";
import { useNavigate } from "react-router-dom";
import DomainInput from "../../../../components/inputs/domainInput";
import { DomainStepView, DomainType } from "../../../../constants";

export interface IUserDomainForm {
  domain: string;
}

interface IUserDomainViewProps {
  domainId: number;
  showCustomSecondStep: (value: boolean) => void;
  domainStatusData: DomainStatusByApiQuery;
  refetchStatusData: () => void;
  stepView: DomainStepView;
  setStepView: (value: DomainStepView) => void;
  urlType: number | null;
  isSubdomainMode?: boolean;
  isEditMode?: boolean;
}

export default function UserDomainView({
  domainId,
  showCustomSecondStep,
  domainStatusData,
  refetchStatusData,
  stepView,
  setStepView,
  urlType,
  isSubdomainMode = false,
  isEditMode = false,
}: IUserDomainViewProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<IUserDomainForm>>(null);
  const userDomain = useRef("");
  const lng = useGetUrlLocale();
  const navigate = useNavigate();

  const [setDomain, { loading: setDomainUrlLoading, error }] =
    useSetDomainUrlMutation();

  const handleAddDomain = () => {
    if (isEditMode) {
      navigate(
        `${lng}/projects/${domainId}/${isSubdomainMode ? "add-subdomain" : "add-domain"}`
      );
    } else {
      showCustomSecondStep(true);
      setStepView(DomainStepView.AddDomain);
    }
  };

  const handleBackToDefault = () => {
    showCustomSecondStep(false);
    setStepView(DomainStepView.Default);
  };

  const onSubmit = async (
    values: IUserDomainForm,
    { setSubmitting }: FormikHelpers<IUserDomainForm>
  ) => {
    try {
      await setDomain({
        variables: {
          domainId,
          urlType: isSubdomainMode
            ? DomainType.UserSubdomain
            : DomainType.UserDomain,
          domainUrl: values.domain,
        },
        onCompleted: () => {
          setStepView(DomainStepView.Connect);
        },
      });
    } catch (error) {}

    setSubmitting(false);
  };

  const initialValues: IUserDomainForm = {
    domain: "",
  };

  const handleSetStep = (value: DomainStepView) => setStepView(value);

  if (urlType !== null)
    return (
      <DomainStatus
        domainId={domainId}
        domainUrl={domainStatusData.domainStatusByApi?.domainUrl}
        status={domainStatusData.domainStatusByApi?.domainStatus}
        refetchDomainData={refetchStatusData}
      />
    );

  if (stepView === DomainStepView.Default)
    return (
      <Row className="d-flex justify-content-center">
        <Col md={isEditMode ? 4 : 12}>
          <Button
            variant="outline-primary"
            className="w-100 fw-normal primary-hover d-flex align-items-center justify-content-center"
            onClick={handleAddDomain}
          >
            <PlusLg className="me-2" />
            {t("projects.Add Domain")}
          </Button>
        </Col>
      </Row>
    );

  if (stepView === DomainStepView.AddDomain)
    return (
      <Col md={8} lg={6} className="mx-auto">
        <Col xs={7} md={12} className="mx-auto px-1 px-md-0 text-center">
          <img
            src={AddDomainLogo}
            loading="lazy"
            alt="Add Domain"
            className="img-fluid mt-5 mb-3 pb-2"
          />
        </Col>

        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={
            isSubdomainMode ? validationSubdomain(t) : validationDomain(t)
          }
        >
          {({ touched, errors, values }) => {
            userDomain.current = values.domain;
            return (
              <FormikForm>
                <DomainInput
                  label={
                    isSubdomainMode
                      ? t("projects.Subdomain Name")
                      : t("projects.Domain Name")
                  }
                  placeholder={
                    isSubdomainMode
                      ? "yoursubdomain.domain.com"
                      : "yourowndomain.com"
                  }
                  errorApollo={error}
                  name="domain"
                  className={
                    touched.domain && errors.domain ? "is-invalid" : ""
                  }
                  style={{ zIndex: 2, backgroundImage: "none" }}
                  autoComplete="off"
                />

                <div className="mt-5 gy-3 d-flex justify-content-center">
                  <Col className="me-3 me-md-4">
                    <Button
                      variant="outline-dark"
                      className="w-100 fw-normal dark-hover"
                      onClick={handleBackToDefault}
                    >
                      {t("support.back")}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      variant="primary"
                      className="w-100 text-white fw-bold order-0 order-lg-1"
                      type="submit"
                      disabled={setDomainUrlLoading}
                    >
                      {t("projects.Continue")}{" "}
                      {setDomainUrlLoading && <Spinner />}
                    </Button>
                  </Col>
                </div>
              </FormikForm>
            );
          }}
        </Formik>
      </Col>
    );

  if (stepView === DomainStepView.Connect)
    return (
      <Col className="mx-auto">
        <DomainConnectView
          userDomain={userDomain.current}
          isSubdomainMode={isSubdomainMode}
          handleSetStep={handleSetStep}
        />
      </Col>
    );

  if (stepView === DomainStepView.Verifying)
    return (
      <Col className="mx-auto">
        <VerifyingInfoView
          handleSetStep={handleSetStep}
          showCustomSecondStep={showCustomSecondStep}
          refetchDomainData={refetchStatusData}
        />
      </Col>
    );
}
